import { getDocs, collection, doc, getDoc, addDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from '../../../firebase/config';
import { fetchCompanyBankId } from '../../companies/service/companiesService';
import { fetchCompaniesData } from '../../companies/service/companiesService';
import type { Company } from '../../companies/service/companiesService';

export type Branch = {
    id: string;
    bankId: string;
    branchCode?: string;
    address?: string;
    psoId?: string;
    [key: string]: any;
};

export type Deployment = {
    id: string;
    bankId: string;
    branchId: string;
    companiesId: string;
    psoId?: string;
    createdBy?: string;
    createdAt?: any;
    updatedAt?: any;
    branch?: Branch | null;
    company?: Company | null;
};

/**
 * Fetch bankId for a user (PSO or Bank)
 */
export async function fetchDeploymentBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    return await fetchCompanyBankId(uid, isBankUser);
}

/**
 * Fetch branches for a bank, optionally filtered by PSO ID
 */
export async function fetchBranches(bankId: string, psoId?: string): Promise<Branch[]> {
    try {
        const branchSnap = await getDocs(collection(db, "banks", bankId, "branch"));
        let branches = branchSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as Branch[];

        // Filter by PSO ID if provided
        if (psoId) {
            branches = branches.filter(branch => branch.psoId === psoId);
        }

        return branches;
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Fetch deployments for a bank, optionally filtered by PSO ID
 */
export async function fetchDeploymentsData(uid: string, isBankUser: boolean = false): Promise<Deployment[]> {
    // Find bankId
    const bankId = await fetchDeploymentBankId(uid, isBankUser);
    if (!bankId) {
        console.log('No bank found for user:', uid);
        return [];
    }

    // Fetch deployments from banks/{bankId}/companyDeployment
    const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
    let deployments = deploymentsSnap.docs.map(doc => ({
        id: doc.id,
        bankId,
        ...doc.data()
    })) as Deployment[];

    // Filter by PSO ID if not bank user
    if (!isBankUser) {
        deployments = deployments.filter(deployment => deployment.psoId === uid);
    }

    // Fetch branches and companies to enrich deployment data
    const branches = await fetchBranches(bankId);
    const companies = await fetchCompaniesData(uid, isBankUser);

    // Enrich deployments with branch and company data
    const enrichedDeployments = deployments.map(deployment => {
        const branch = branches.find(b => b.id === deployment.branchId);
        const company = companies.find(c => c.id === deployment.companiesId);
        return {
            ...deployment,
            branch: branch || null,
            company: company || null
        };
    });

    // Sort by branch code, then by company type
    enrichedDeployments.sort((a, b) => {
        const branchCodeA = a.branch?.branchCode || '';
        const branchCodeB = b.branch?.branchCode || '';
        const branchCompare = branchCodeA.localeCompare(branchCodeB);
        if (branchCompare !== 0) return branchCompare;

        const companyTypeA = a.company?.companyType || '';
        const companyTypeB = b.company?.companyType || '';
        return companyTypeA.localeCompare(companyTypeB);
    });

    return enrichedDeployments;
}

/**
 * Create a new deployment
 */
export async function createDeployment(
    bankId: string,
    branchId: string,
    companiesId: string,
    psoId: string
): Promise<string> {
    try {
        const deploymentRef = await addDoc(collection(db, "banks", bankId, "companyDeployment"), {
            branchId,
            companiesId,
            psoId,
            createdBy: psoId,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return deploymentRef.id;
    } catch (error) {
        console.error('Error creating deployment:', error);
        throw error;
    }
}

/**
 * Update deployment company
 */
export async function updateDeploymentCompany(
    bankId: string,
    deploymentId: string,
    companiesId: string
): Promise<void> {
    try {
        const deploymentRef = doc(db, "banks", bankId, "companyDeployment", deploymentId);
        await updateDoc(deploymentRef, {
            companiesId,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating deployment company:', error);
        throw error;
    }
}

/**
 * Update an existing deployment
 */
export async function updateDeployment(
    bankId: string,
    deploymentId: string,
    companiesId: string
): Promise<void> {
    try {
        const deploymentRef = doc(db, "banks", bankId, "companyDeployment", deploymentId);
        await updateDoc(deploymentRef, {
            companiesId,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating deployment:', error);
        throw error;
    }
}

/**
 * Delete a deployment
 */
export async function deleteDeployment(bankId: string, deploymentId: string): Promise<void> {
    try {
        const deploymentRef = doc(db, "banks", bankId, "companyDeployment", deploymentId);
        await deleteDoc(deploymentRef);
    } catch (error) {
        console.error('Error deleting deployment:', error);
        throw error;
    }
}

/**
 * Check if a deployment conflict exists (same company type already deployed to branch)
 */
export async function checkDeploymentConflict(
    bankId: string,
    branchId: string,
    companiesId: string,
    excludeDeploymentId?: string
): Promise<{ hasConflict: boolean; existingDeployment?: Deployment; existingCompany?: Company }> {
    try {
        // Fetch all deployments for this branch
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const deployments = deploymentsSnap.docs
            .map(doc => ({ id: doc.id, bankId, ...doc.data() } as Deployment))
            .filter(d => d.branchId === branchId && d.id !== excludeDeploymentId);

        // Fetch the company being deployed
        const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
        const companiesList = companiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        const targetCompany = companiesList.find(c => c.id === companiesId);
        if (!targetCompany) {
            return { hasConflict: false };
        }

        // Check if target company has companyId to fetch from global collection
        let targetCompanyType = targetCompany.companyType;
        if (targetCompany.companyId) {
            try {
                const globalCompanyDoc = await getDoc(doc(db, "companies", targetCompany.companyId));
                if (globalCompanyDoc.exists()) {
                    const globalData = globalCompanyDoc.data();
                    targetCompanyType = targetCompany.companyType || globalData.companyType;
                }
            } catch (err) {
                console.error('Error fetching global company:', err);
            }
        }

        // Check if any existing deployment has a company of the same type
        for (const deployment of deployments) {
            const existingCompany = companiesList.find(c => c.id === deployment.companiesId);
            if (!existingCompany) continue;

            let existingCompanyType = existingCompany.companyType;
            if (existingCompany.companyId) {
                try {
                    const globalCompanyDoc = await getDoc(doc(db, "companies", existingCompany.companyId));
                    if (globalCompanyDoc.exists()) {
                        const globalData = globalCompanyDoc.data();
                        existingCompanyType = existingCompany.companyType || globalData.companyType;
                    }
                } catch (err) {
                    console.error('Error fetching global company:', err);
                }
            }

            if (existingCompanyType === targetCompanyType) {
                return {
                    hasConflict: true,
                    existingDeployment: deployment,
                    existingCompany: existingCompany as Company
                };
            }
        }

        return { hasConflict: false };
    } catch (error) {
        console.error('Error checking deployment conflict:', error);
        throw error;
    }
}

